/**
 * SplitPay screen — the per-guest "pay your share" surface.
 *
 * Lifecycle:
 *   1. Load order via orderApi.get(orderId) and existing splits via
 *      paymentSplitApi.list(orderId).
 *   2. Hydrate the local reducer from order-line attribution: each line's
 *      addedByGuestId becomes the default sole sharer (so a guest who only
 *      added line L1 starts with L1 already on their plate).
 *   3. Render the active layout (list / pie / receipt) which lets the user
 *      tap to claim/unclaim or open SplitSheet for arbitrary N-way splits.
 *   4. On primary CTA we call paymentSplitApi.create with the local guest's
 *      claimed lines (computed via deriveClaimedLines). For card we then
 *      fire checkoutCard and bounce to the PSP redirect; for cash we
 *      generate a short code via mark-cash-pending and route to the cash
 *      follow-up screen.
 */
import { allocateToTotal } from "@/lib/split-allocation";
import { splitCheckoutAction, splitReturnUrl, type SplitIntentLike } from "@/lib/split-checkout";
import { cleanupPaymob, launchPaymobPixel } from "@/lib/paymob";
import { BottomSheet } from "@/components/group/BottomSheet";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { TopBar } from "@/components/ui";
import {
  paymentSplitApi,
  PaymentSplitOverCoverage,
  PaymentSplitUncovered,
  type PaymentSplit,
  type CoverageReport,
} from "@/api/payment-split";
import { ApiError } from "@/api/http";
import { orderApi } from "@/api/services";
import type { OrderOutput } from "@/api/online-ordering-service/model";
import { useGroupCart } from "@/store/group-cart";
import {
  deriveClaimedLines,
  rosterFromCart,
  splitReducer,
  summarise,
  initialSplitState,
  type SplitMethod,
} from "@/store/split-pay";
import { useReducer } from "react";
import { useFormatPrice } from "@/hooks/useFormatPrice";
import { useAuth } from "@/store/auth";
import { Price } from "@/components/Price";
import { payerForOrder } from "@/lib/payer-token";
import { getToken } from "@/api/http";
import { readParticipant } from "@/lib/participant-token";
import { PayerNameSheet } from "@/components/dinein/PayerNameSheet";
import { OrderAccessError } from "@/components/dinein/OrderAccessError";
import { ShareBillButton } from "@/components/dinein/ShareBillButton";
import { classifyOrderError, type OrderErrorKind } from "@/lib/order-error";
import { useTable } from "@/store/table";
import { SignInModal } from "@/components/SignInModal";

function injectPrice(text: string, amount: number) {
  const parts = text.split("___PRICE___");
  if (parts.length === 1) return text;
  return (
    <span style={{ display: "inline-flex", alignItems: "baseline", gap: 3 }}>
      {parts[0]}
      <Price amount={amount} />
      {parts[1]}
    </span>
  );
}
import { splitPayLayout } from "@/lib/feature-flags";
import {
  trackCoverageBlocked,
  trackSplitClaimedLine,
  trackSplitCreated,
  trackSplitSharedLine,
  trackSplitUnclaimedLine,
} from "@/lib/analytics-events";
import { ListVariation } from "./SplitPay/ListVariation";
import { PieVariation } from "./SplitPay/PieVariation";
import { ReceiptVariation } from "./SplitPay/ReceiptVariation";
import { SplitSheet } from "./SplitPay/SplitSheet";
import type { LineRow } from "./SplitPay/types";
import type { GroupCartParticipant } from "@/api/group-cart";

const SELF_ID = "self";

interface OrderLineWithAttribution {
  lineId?: string | null;
  addedByGuestId?: string | null;
  addedByDisplayName?: string | null;
  itemName?: { en?: string; ar?: string } | null;
  quantity?: number;
  pricingSummary?: {
    totalAmount?: number;
    subtotalAmount?: number;
    taxAmount?: number;
  };
}

const SPLIT_PAY_SHEET_ID = "paymob-payment-container";

function flattenOrder(order: OrderOutput, lang: string): LineRow[] {
  const lines = ((order.orderLines ?? []) as OrderLineWithAttribution[]).filter(
    (ln) => !!ln.lineId,
  );
  // Each line's share of the bill: the order total split by the lines' own
  // amounts, exact to the halala (scaling by total/itemsSubtotal drifted from
  // the bill whenever line and order pricing disagreed: 97.91 for 107.20).
  const shares = allocateToTotal(
    lines.map((ln) => ln.pricingSummary?.totalAmount ?? 0),
    order.pricingSummary?.totalAmount ?? 0,
  );
  return lines.map((ln, i) => ({
    lineId: ln.lineId as string,
    itemName:
      ln.itemName?.[lang as "en" | "ar"] ??
      ln.itemName?.en ??
      ln.itemName?.ar ??
      (ln.lineId as string),
    quantity: Math.max(1, Math.round(ln.quantity ?? 1)),
    totalAmount: shares[i],
    addedByGuestId: ln.addedByGuestId ?? null,
    addedByDisplayName: ln.addedByDisplayName ?? null,
  }));
}

/**
 * When the order was placed solo (no group), there are no participants on
 * the cart — synthesise a single self-participant so the variations have
 * something to render.
 */
/**
 * Map over-covered line ids back to item names so the inline error reads
 * "Already paid: Margherita pizza, Coke" instead of "abc123, def456". Falls
 * back to the raw id when the line isn't in the local order (defensive — a
 * concurrent line removal could orphan it).
 */
function formatOverCoverage(
  overCoveredLineIds: string[],
  lines: LineRow[],
): string {
  if (overCoveredLineIds.length === 0) return "";
  const byId = new Map(lines.map((l) => [l.lineId, l.itemName] as const));
  const names = overCoveredLineIds.map((id) => byId.get(id) ?? id);
  return `Already paid: ${names.join(", ")}`;
}

function syntheticParticipantFor(
  displayName: string,
  guestId: string = SELF_ID,
): GroupCartParticipant {
  return {
    guestId,
    displayName,
    verified: true,
    customerId: null,
    colorIndex: 0,
    joinedAt: new Date().toISOString(),
    lastSeenAt: new Date().toISOString(),
  };
}

function hasUsableIdentity(orderId: string | undefined): boolean {
  return !!getToken() || !!readParticipant() || !!payerForOrder(orderId ?? null);
}

export function SplitPayScreen() {
  const { t } = useTranslation();
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { format } = useFormatPrice();
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const { cart: groupCartState, participant: storedParticipant } = useGroupCart();

  const [order, setOrder] = useState<OrderOutput | null>(null);
  const [splits, setSplits] = useState<PaymentSplit[]>([]);
  const [state, dispatch] = useReducer(splitReducer, initialSplitState);
  const [submitting, setSubmitting] = useState(false);
  // The embedded Paymob card form for a card share in checkout.
  const [paySheet, setPaySheet] = useState<{ splitId: string; intent: SplitIntentLike } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uncoveredFromServer, setUncoveredFromServer] = useState<string[] | null>(null);
  const [serverCoverage, setServerCoverage] = useState<CoverageReport | null>(null);
  const [splitSheetLineId, setSplitSheetLineId] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const isFullBill = searchParams.get("full") === "1";
  const [hydrated, setHydrated] = useState(false);
  const [identityReady, setIdentityReady] = useState<boolean>(() =>
    hasUsableIdentity(orderId),
  );
  /** Load failure of the ORDER itself (vs `error`, which is checkout copy). */
  const [loadErrorKind, setLoadErrorKind] = useState<OrderErrorKind | null>(null);
  const [signInOpen, setSignInOpen] = useState(false);
  /** Bumped by the Retry CTA to re-run the initial load effect. */
  const [reloadNonce, setReloadNonce] = useState(0);
  const { tableInfo } = useTable();
  const hasTableContext = !!tableInfo?.branchId && !!tableInfo?.tableId;

  // The local guest's identity in this split context, in precedence order:
  // group participant > dine-in payer session > synthetic SELF_ID (signed-in
  // solo customer — the BFF derives their identity from the customer JWT).
  const payer = useMemo(() => payerForOrder(orderId), [orderId, identityReady]);
  const selfId = storedParticipant?.guestId ?? payer?.guestId ?? SELF_ID;

  const lang = (i18n.language || "en").startsWith("ar") ? "ar" : "en";
  const lines = useMemo(() => (order ? flattenOrder(order, lang) : []), [
    order,
    lang,
  ]);

  // Roster: if a group is active, use its participants; else synth a self
  // participant from the customer profile or "You" fallback.
  const participants = useMemo<GroupCartParticipant[]>(() => {
    if (groupCartState && groupCartState.participants.length > 0) {
      return rosterFromCart(
        groupCartState.participants,
        groupCartState.createdByGuestId,
      );
    }
    const display = payer?.displayName ?? user?.name ?? "You";
    return [syntheticParticipantFor(display, payer?.guestId ?? SELF_ID)];
  }, [groupCartState, user, payer]);

  const participantById = useMemo(() => {
    const m = new Map<string, GroupCartParticipant>();
    for (const p of participants) m.set(p.guestId, p);
    return m;
  }, [participants]);

  // Initial load: order + existing splits in parallel.
  useEffect(() => {
    if (!orderId || !identityReady) return;
    let cancelled = false;
    setLoadErrorKind(null);
    Promise.all([orderApi.get(orderId), paymentSplitApi.list(orderId)])
      .then(([orderRes, splitsRes]) => {
        if (cancelled) return;
        setOrder(orderRes);
        setSplits(splitsRes.splits);
        setServerCoverage(splitsRes.coverage);
      })
      .catch((err) => {
        if (cancelled) return;
        // Never surface the raw backend string to a diner — classify it.
        setLoadErrorKind(classifyOrderError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, identityReady, reloadNonce]);

  // Hydrate reducer once we have lines (only on first load to avoid clobbering
  // user picks if the order is re-fetched). Re-fetching splits doesn't reset
  // the local state.
  useEffect(() => {
    if (hydrated || lines.length === 0 || !serverCoverage) return;
    const seed = lines.map((ln) => {
      let added =
        ln.addedByGuestId && participantById.has(ln.addedByGuestId)
          ? ln.addedByGuestId
          : null;
      if (isFullBill && serverCoverage.uncoveredLineIds.includes(ln.lineId)) {
        added = selfId;
      }
      return {
        lineId: ln.lineId,
        addedByGuestId: added,
      };
    });
    dispatch({ type: "hydrate_from_attribution", orderLines: seed });
    setHydrated(true);
  }, [lines, participantById, hydrated, isFullBill, selfId, serverCoverage]);

  const summary = useMemo(
    () =>
      summarise(
        state,
        selfId,
        lines.map((l) => ({ lineId: l.lineId, totalAmount: l.totalAmount })),
      ),
    [state, selfId, lines],
  );

  const onToggleSelf = (lineId: string) => {
    const before = state.sharers[lineId] ?? [];
    const wasOnIt = before.includes(selfId);
    dispatch({ type: "toggle_self", lineId, selfId });
    setUncoveredFromServer(null);
    // Telemetry: distinguish claim vs unclaim vs join-share. Mirrors the
    // four cases in the reducer; we read `before` to infer the resulting
    // size cheaply without re-running the reducer.
    if (orderId) {
      if (before.length === 0) {
        trackSplitClaimedLine({ orderId, lineId, totalSharers: 1 });
      } else if (wasOnIt && before.length === 1) {
        trackSplitUnclaimedLine({ orderId, lineId, totalSharers: 0 });
      } else if (wasOnIt) {
        trackSplitUnclaimedLine({
          orderId,
          lineId,
          totalSharers: before.length - 1,
        });
      } else {
        trackSplitSharedLine({
          orderId,
          lineId,
          sharers: before.length + 1,
        });
      }
    }
  };

  const onOpenSplitSheet = (lineId: string) => {
    setSplitSheetLineId(lineId);
  };

  const splitSheetLine = lines.find((l) => l.lineId === splitSheetLineId) ?? null;

  const handleCardCheckout = async () => {
    if (!order || !orderId) return;
    setSubmitting(true);
    setError(null);
    setUncoveredFromServer(null);
    try {
      const claimedLines = deriveClaimedLines(state, selfId);
      if (claimedLines.length === 0) {
        setError(t("v2.split_pay.coverage_strict_warning"));
        return;
      }
      const split = await paymentSplitApi.create({
        branchId: order.branchId ?? "",
        orderId,
        method: "card",
        claimedLines,
      });
      trackSplitCreated({
        orderId,
        splitId: split.splitId,
        method: "card",
        claimedLineCount: claimedLines.length,
        myShareAmount: summary.myShare,
        inGroup: !!groupCartState,
      });
      // configurationId is left unset on purpose — payment_service falls
      // back to the tenant's first active card-type provider config when
      // the client doesn't supply one (saves the customer app from having
      // to know about per-PSP config IDs). currency comes off the order
      // itself so we never round-trip an SAR-default to a non-SAR tenant.
      const intent = await paymentSplitApi.checkoutCard(split.splitId, {
        currency: order.currency ?? "SAR",
        // Where Paymob brings the guest back after 3DS: this storefront's
        // bill, not the default production menu link.
        returnUrl: splitReturnUrl(window.location.origin, window.location.pathname, orderId, split.splitId),
      });
      const next = splitCheckoutAction(intent as SplitIntentLike);
      if (next.kind === "pixel") {
        setPaySheet({ splitId: split.splitId, intent: intent as SplitIntentLike });
        return;
      }
      if (next.kind === "redirect") {
        window.location.href = next.url;
        return;
      }
      // Paid already (saved card) — the bill polls for split state.
      navigate(`/dine-in-order/${orderId}`, { replace: true });
    } catch (err) {
      if (err instanceof PaymentSplitUncovered) {
        setUncoveredFromServer(err.uncoveredLineIds);
        setError(t("v2.split_pay.coverage_strict_warning"));
        if (orderId) {
          trackCoverageBlocked({
            orderId,
            uncoveredLineCount: err.uncoveredLineIds.length,
            method: "card",
          });
        }
      } else if (err instanceof PaymentSplitOverCoverage) {
        setError(formatOverCoverage(err.overCoveredLineIds, lines));
      } else if (err instanceof ApiError && err.status === 422) {
        setError("Payment configuration missing — staff must complete setup.");
      } else {
        setError((err as Error).message ?? "Checkout failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCashCheckout = async () => {
    if (!order || !orderId) return;
    setSubmitting(true);
    setError(null);
    try {
      const claimedLines = deriveClaimedLines(state, selfId);
      if (claimedLines.length === 0) {
        setError(t("v2.split_pay.coverage_strict_warning"));
        return;
      }
      const split = await paymentSplitApi.create({
        branchId: order.branchId ?? "",
        orderId,
        method: "cash",
        claimedLines,
      });
      trackSplitCreated({
        orderId,
        splitId: split.splitId,
        method: "cash",
        claimedLineCount: claimedLines.length,
        myShareAmount: summary.myShare,
        inGroup: !!groupCartState,
      });
      // Customers ask the server to flip draft → awaiting_collection so a
      // 4-char short code is generated and shown to the waiter.
      await paymentSplitApi.markCashPending(split.splitId);
      navigate(`/orders/${orderId}/cash/${split.splitId}`);
    } catch (err) {
      if (err instanceof PaymentSplitOverCoverage) {
        setError(formatOverCoverage(err.overCoveredLineIds, lines));
      } else {
        setError((err as Error).message ?? "Failed to start cash payment");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Mount the Paymob card form when the sheet opens. Paid → the bill (the
  // webhook marks the share paid); cancelled → give the lines back.
  useEffect(() => {
    if (!paySheet) return;
    const sheet = paySheet;
    const timer = setTimeout(() => {
      launchPaymobPixel(sheet.intent as never, {
        elementId: SPLIT_PAY_SHEET_ID,
        onSuccess: () => {
          navigate(`/dine-in-order/${orderId}?paymentReturn=1&splitId=${sheet.splitId}`, { replace: true });
        },
        onCancel: () => void cancelPaySheet(sheet.splitId),
        onFailure: () => void cancelPaySheet(sheet.splitId),
      }).catch(() => void cancelPaySheet(sheet.splitId));
    }, 80);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paySheet]);

  async function cancelPaySheet(splitId: string) {
    cleanupPaymob(SPLIT_PAY_SHEET_ID);
    setPaySheet(null);
    try {
      await paymentSplitApi.release(splitId, "checkout_cancelled");
    } catch {
      /* the janitor releases it when the processing window expires */
    }
    setSubmitting(false);
  }

  const layout = splitPayLayout();
  const Variation =
    layout === "list"
      ? ListVariation
      : layout === "receipt"
        ? ReceiptVariation
        : PieVariation;

  if (!order) {
    // 401 while we DO hold a table QR → the payer sheet is the remedy (stale
    // payer token bound to a previous order); show it instead of an error.
    const needsIdentity = !identityReady || loadErrorKind === "identity";
    // The sheet can only help when a QR gave us branch+table to mint against.
    const showSheet = needsIdentity && hasTableContext;
    // Without table context there's nothing to mint — offer sign-in instead.
    const cardKind: OrderErrorKind | null = needsIdentity
      ? hasTableContext
        ? null
        : "identity"
      : loadErrorKind;
    return (
      <div style={{ padding: 24, textAlign: "center", color: "var(--ink-3)" }}>
        {cardKind ? (
          <OrderAccessError
            kind={cardKind}
            onSignIn={() => setSignInOpen(true)}
            onRetry={() => setReloadNonce((n) => n + 1)}
            onBack={() => navigate(`/dine-in-order/${orderId}`)}
          />
        ) : identityReady && !loadErrorKind ? (
          "…"
        ) : null}
        <PayerNameSheet
          open={showSheet && !!orderId}
          orderId={orderId ?? ""}
          onEstablished={() => {
            setIdentityReady(true);
            setLoadErrorKind(null);
            setReloadNonce((n) => n + 1);
          }}
          onCancel={() => navigate(`/dine-in-order/${orderId}`)}
        />
        <SignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />
      </div>
    );
  }

  const variationProps = {
    state,
    selfId,
    participants,
    lines,
    participantById,
    splits,
    onToggleSelf,
    onOpenSplitSheet,
    format: (n: number) => <Price amount={n} />,
  };

  const showServerUncovered =
    uncoveredFromServer && uncoveredFromServer.length > 0;

  return (
    <div style={{ paddingBottom: 140 }}>
      <TopBar
        title={t("v2.split_pay.title")}
        onBack={() => navigate(`/dine-in-order/${orderId}`)}
      />

      {/* Sticky summary card */}
      <div style={{ padding: "12px 16px 0" }}>
        <div
          style={{
            background: "var(--surface-1)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-card)",
            padding: "12px 14px",
            boxShadow: "0 6px 18px -10px rgba(0,0,0,0.18)",
          }}
        >
          <div
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <div style={{ fontSize: 12, color: "var(--ink-3)", fontWeight: 600 }}>
              {t("v2.split_pay.your_total")}
            </div>
            <div style={{ fontSize: 12, color: "var(--ink-3)", fontWeight: 600 }}>
              {t("v2.split_pay.remaining_for_table")}
            </div>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              marginTop: 2,
            }}
          >
            <div className="display"
              style={{ fontSize: 22, fontWeight: 800, color: "var(--ink-1)" }}
            >
              <Price amount={summary.myShare} />
            </div>
            <div className="display"
              style={{ fontSize: 14, color: "var(--ink-2)", fontWeight: 700 }}
            >
              <Price amount={summary.remainingForTable} />
            </div>
          </div>
          {summary.uncoveredLineIds.length > 0 && (
            <div
              style={{
                marginTop: 8,
                fontSize: 11,
                color: "#92400e",
                background: "#fef3c7",
                padding: "6px 10px",
                borderRadius: 8,
              }}
            >
              {t("v2.split_pay.coverage_uncovered_count", {
                count: summary.uncoveredLineIds.length,
                defaultValue: `${summary.uncoveredLineIds.length} items still unclaimed`,
              })}
            </div>
          )}
          {showServerUncovered && (
            <div
              style={{
                marginTop: 8,
                fontSize: 11,
                color: "#b91c1c",
                background: "#fee2e2",
                padding: "6px 10px",
                borderRadius: 8,
              }}
            >
              {t("v2.split_pay.coverage_strict_warning")}
            </div>
          )}
        </div>
      </div>

      <Variation {...variationProps} />

      {/* Invite anyone to view and pay this table's bill (spec §5.2). */}
      <div style={{ padding: "4px 16px 0", display: "flex" }}>
        <ShareBillButton
          orderId={orderId}
          displayNameHint={user?.name ?? null}
          variant="soft"
          size="sm"
          full
        />
      </div>

      {error && !showServerUncovered && (
        <div
          role="alert"
          style={{
            margin: "12px 16px",
            padding: "10px 12px",
            background: "#fee2e2",
            color: "#b91c1c",
            borderRadius: 10,
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}

      <SplitSheet
        open={!!splitSheetLine}
        onClose={() => setSplitSheetLineId(null)}
        lineId={splitSheetLine?.lineId ?? null}
        lineLabel={splitSheetLine?.itemName ?? ""}
        lineTotal={splitSheetLine?.totalAmount ?? 0}
        participants={participants}
        selfId={selfId}
        initialSharers={
          splitSheetLine
            ? state.sharers[splitSheetLine.lineId] ?? [selfId]
            : [selfId]
        }
        format={(n) => format(n) ?? `${n}`}
        onConfirm={(sharers) => {
          if (!splitSheetLine) return;
          dispatch({
            type: "set_split",
            lineId: splitSheetLine.lineId,
            sharers,
          });
        }}
      />

      {/* Sticky bottom: method toggle + primary CTA */}
      <div
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          padding: "10px 16px calc(env(safe-area-inset-bottom, 0px) + 12px)",
          background: "var(--surface-1)",
          borderTop: "1px solid var(--border)",
          boxShadow: "0 -8px 24px -12px rgba(0,0,0,0.18)",
        }}
      >
        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          {(["card", "cash"] as SplitMethod[]).map((m) => (
            <button
              key={m}
              data-testid={`split-method-${m}`}
              onClick={() => dispatch({ type: "set_method", method: m })}
              style={{
                flex: 1,
                height: 38,
                borderRadius: 10,
                border:
                  state.method === m
                    ? "1.5px solid var(--ink-1)"
                    : "1px solid var(--border)",
                background:
                  state.method === m ? "var(--ink-1)" : "var(--surface-1)",
                color: state.method === m ? "#fff" : "var(--ink-1)",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {t(
                m === "card"
                  ? "v2.split_pay.method_card"
                  : "v2.split_pay.method_cash",
              )}
            </button>
          ))}
        </div>
        <button
          data-testid={state.method === "card" ? "pay-card" : "pay-cash"}
          data-my-share={summary.myShare}
          onClick={
            state.method === "card" ? handleCardCheckout : handleCashCheckout
          }
          disabled={submitting || summary.myShare <= 0}
          style={{
            width: "100%",
            height: 50,
            borderRadius: 12,
            background:
              submitting || summary.myShare <= 0
                ? "var(--surface-2-strong, #e5e7eb)"
                : "var(--primary-600)",
            color: "#fff",
            border: "none",
            fontSize: 15,
            fontWeight: 800,
            cursor: submitting ? "wait" : "pointer",
          }}
        >
          {submitting
            ? "…"
            : state.method === "card"
              ? injectPrice(t("v2.split_pay.pay_with_card", { amount: "___PRICE___" }), summary.myShare)
              : injectPrice(t("v2.split_pay.tell_waiter_cash", { amount: "___PRICE___" }), summary.myShare)}
        </button>
      </div>

      <BottomSheet
        open={!!paySheet}
        onClose={() => paySheet && void cancelPaySheet(paySheet.splitId)}
        ariaLabel="Pay with card"
      >
        <div data-testid="split-pay-sheet" style={{ padding: "8px 16px 16px" }}>
          <div id={SPLIT_PAY_SHEET_ID} style={{ minHeight: 320 }} />
          <button
            data-testid="split-pay-cancel"
            onClick={() => paySheet && void cancelPaySheet(paySheet.splitId)}
            style={{ marginTop: 8, width: "100%", height: 40, background: "transparent", border: 0, color: "var(--ink-2)", fontWeight: 600, cursor: "pointer" }}
          >
            {t("v2.common.cancel", "Cancel")}
          </button>
        </div>
      </BottomSheet>

      <PayerNameSheet
        open={!identityReady && !!orderId}
        orderId={orderId ?? ""}
        onEstablished={() => setIdentityReady(true)}
        onCancel={() => navigate(`/dine-in-order/${orderId}`)}
      />
    </div>
  );
}
